import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import {
  Bus,
  Car,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CircleDot,
  FilterX,
  Home,
  MoreHorizontal,
  Pencil,
  Plus,
  Route,
  Search,
  Trash2,
} from 'lucide-react';
import apiService from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';
import { downloadPdfBase64 } from '../utils/pdfDownload';
import BoardingsByRide from './transport/BoardingsByRide';
import PatientLookupSelect from './transport/PatientLookupSelect';
import CompanionLookupSelect from './transport/CompanionLookupSelect';
import { getTransportPermission } from '../utils/transportPermissions';

const RESOURCE_LABELS = {
  cities: 'Cidades',
  drivers: 'Motoristas',
  vehicles: 'Veículos',
  patients: 'Passageiros',
  facilities: 'Estabelecimentos',
  specialties: 'Especialidades',
  distances: 'Distâncias',
  rides: 'Corridas',
  boardings: 'Embarques',
};

const RESOURCE_ORDER = [
  'rides',
  'boardings',
  'drivers',
  'vehicles',
  'patients',
  'cities',
  'facilities',
  'specialties',
  'distances',
];

const LAST_TRANSPORT_TAB_KEY = 'transportActiveResource';

function resourceFromPath(pathname) {
  const segment = String(pathname || '').split('/').filter(Boolean).pop();
  return RESOURCE_ORDER.includes(segment) ? segment : null;
}

const COLUMN_LABELS = {
  name: 'Nome',
  ibge_code: 'IBGE',
  state_code: 'UF',
  active: 'Ativo',
  cpf: 'CPF',
  mobile_phone: 'Celular',
  status: 'Status',
  model: 'Modelo',
  plate: 'Placa',
  capacity: 'Lotação',
  cns: 'CNS',
  cnes: 'CNES',
  city_id: 'Cidade',
  description: 'Descrição',
  origin_city_id: 'Origem',
  destination_city_id: 'Destino',
  distance_km: 'Distância (km)',
  starts_at: 'Saída',
  driver_id: 'Motorista',
  vehicle_id: 'Veículo',
  departure_at: 'Data saída',
  patient_id: 'Passageiro',
  ride_id: 'Corrida',
  birth_date: 'Nascimento',
};

const FIELD_CONFIG = {
  cities: [
    { name: 'name', label: 'Nome', type: 'text', required: true },
    { name: 'ibge_code', label: 'Codigo IBGE', type: 'text' },
    { name: 'state_code', label: 'UF', type: 'text' },
    { name: 'active', label: 'Ativo', type: 'checkbox' },
  ],
  drivers: [
    { name: 'name', label: 'Nome', type: 'text', required: true },
    { name: 'cpf', label: 'CPF', type: 'cpf' },
    { name: 'registration', label: 'Matricula', type: 'text' },
    { name: 'driver_license', label: 'CNH', type: 'text' },
    { name: 'driver_license_category', label: 'Categoria CNH', type: 'text' },
    { name: 'mobile_phone', label: 'Celular', type: 'text' },
    { name: 'email', label: 'Email', type: 'email' },
    { name: 'city_id', label: 'Cidade', type: 'select', resource: 'cities', labelField: 'name' },
    { name: 'status', label: 'Status', type: 'select-static', options: ['active', 'inactive'] },
    { name: 'in_use', label: 'Em uso', type: 'checkbox' },
    { name: 'notes', label: 'Observacoes', type: 'textarea' },
  ],
  vehicles: [
    { name: 'model', label: 'Modelo', type: 'text', required: true },
    { name: 'plate', label: 'Placa', type: 'text', required: true },
    { name: 'manufacturer', label: 'Fabricante', type: 'text' },
    { name: 'manufacture_year', label: 'Ano', type: 'number' },
    { name: 'capacity', label: 'Lotacao', type: 'number', required: true },
    { name: 'fuel_type', label: 'Combustivel', type: 'text' },
    { name: 'vehicle_type', label: 'Tipo', type: 'text' },
    { name: 'color', label: 'Cor', type: 'text' },
    { name: 'status', label: 'Status', type: 'select-static', options: ['active', 'inactive', 'maintenance'] },
    { name: 'in_use', label: 'Em uso', type: 'checkbox' },
    { name: 'notes', label: 'Observacoes', type: 'textarea' },
  ],
  patients: [
    { name: 'cpf', label: 'CPF', type: 'cpf', required: true, section: 'Passageiro' },
    { name: 'name', label: 'Nome', type: 'text', required: true },
    { name: 'cns', label: 'CNS', type: 'text' },
    { name: 'birth_date', label: 'Nascimento', type: 'date' },
    { name: 'gender', label: 'Sexo', type: 'select-static', options: ['male', 'female', 'other'] },
    { name: 'mother_name', label: 'Nome da mae', type: 'text' },
    { name: 'city_id', label: 'Cidade', type: 'select', resource: 'cities', labelField: 'name' },
    { name: 'zip_code', label: 'CEP', type: 'cep' },
    { name: 'street', label: 'Endereco', type: 'text', homePickup: true },
    { name: 'mobile_phone', label: 'Celular', type: 'text' },
    {
      name: 'boarding_point',
      label: 'Local de embarque',
      type: 'text',
      full: true,
      hint: 'Digite o ponto onde o passageiro embarca. Na hemodiálise, use o ícone de casa no endereço quando a busca for em casa.',
    },
    { name: 'pickup_at_home', label: 'Busca em casa', type: 'hidden' },
    { name: 'companion_document', label: 'CPF', type: 'cpf', lookupMode: 'companion', section: 'Acompanhante' },
    { name: 'companion_name', label: 'Nome', type: 'text' },
    { name: 'companion_cns', label: 'CNS', type: 'text' },
    { name: 'companion_birth_date', label: 'Nascimento', type: 'date' },
    { name: 'companion_gender', label: 'Sexo', type: 'select-static', options: ['male', 'female', 'other'] },
    { name: 'companion_mother_name', label: 'Nome da mae', type: 'text' },
    { name: 'companion_city_id', label: 'Cidade', type: 'select', resource: 'cities', labelField: 'name' },
    { name: 'companion_zip_code', label: 'CEP', type: 'cep' },
    { name: 'companion_street', label: 'Endereco', type: 'text' },
    { name: 'companion_phone', label: 'Celular', type: 'text' },
    { name: 'accessibility', label: 'Acessibilidade', type: 'text', section: 'Outros' },
    { name: 'active', label: 'Ativo', type: 'checkbox' },
  ],
  facilities: [
    { name: 'name', label: 'Nome', type: 'text', required: true },
    { name: 'trade_name', label: 'Nome fantasia', type: 'text' },
    { name: 'alias', label: 'Apelido', type: 'text' },
    { name: 'cnes', label: 'CNES', type: 'text' },
    { name: 'city_id', label: 'Cidade', type: 'select', resource: 'cities', labelField: 'name' },
    { name: 'facility_type', label: 'Tipo', type: 'text' },
    { name: 'active', label: 'Ativo', type: 'checkbox' },
  ],
  specialties: [
    { name: 'description', label: 'Descricao', type: 'text', required: true },
    { name: 'active', label: 'Ativo', type: 'checkbox' },
  ],
  distances: [
    { name: 'origin_city_id', label: 'Origem', type: 'select', resource: 'cities', labelField: 'name', required: true },
    { name: 'destination_city_id', label: 'Destino', type: 'select', resource: 'cities', labelField: 'name', required: true },
    { name: 'distance_km', label: 'Distancia KM', type: 'number', required: true },
    { name: 'amount', label: 'Valor', type: 'number' },
    { name: 'overnight_amount', label: 'Valor pernoite', type: 'number' },
    { name: 'active', label: 'Ativo', type: 'checkbox' },
  ],
  rides: [
    { name: 'starts_at', label: 'Saida', type: 'datetime-local', required: true },
    { name: 'ends_at', label: 'Retorno', type: 'datetime-local', required: true },
    { name: 'origin_city_id', label: 'Origem', type: 'select', resource: 'cities', labelField: 'name', required: true },
    { name: 'destination_city_id', label: 'Destino', type: 'select', resource: 'cities', labelField: 'name', required: true },
    { name: 'driver_id', label: 'Motorista', type: 'select', resource: 'drivers', labelField: 'name', required: true },
    { name: 'vehicle_id', label: 'Veiculo', type: 'select', resource: 'vehicles', labelField: 'model', secondaryField: 'plate', required: true },
    { name: 'capacity', label: 'Lotacao', type: 'number' },
    { name: 'transport_type', label: 'Tipo transporte', type: 'select-static', options: ['0', '1', '2', '3', '4', '5', '6'] },
    { name: 'boarding_point', label: 'Local de embarque', type: 'textarea' },
    { name: 'incident_notes', label: 'Intercorrencia', type: 'textarea' },
    { name: 'status', label: 'Status', type: 'select-static', options: ['scheduled', 'completed', 'cancelled'] },
    { name: 'available', label: 'Disponivel', type: 'checkbox' },
  ],
  boardings: [
    { name: 'ride_id', label: 'Corrida', type: 'select', resource: 'rides', labelField: 'starts_at', secondaryField: 'plate' },
    { name: 'patient_id', label: 'Passageiro', type: 'select', resource: 'patients', labelField: 'name', required: true },
    { name: 'destination_city_id', label: 'Destino', type: 'select', resource: 'cities', labelField: 'name', required: true },
    { name: 'facility_id', label: 'Estabelecimento', type: 'select', resource: 'facilities', labelField: 'name' },
    { name: 'specialty_id', label: 'Especialidade', type: 'select', resource: 'specialties', labelField: 'description' },
    { name: 'departure_at', label: 'Data saida', type: 'datetime-local', required: true },
    { name: 'appointment_time', label: 'Horario atendimento', type: 'time' },
    { name: 'patient_phone', label: 'Telefone passageiro', type: 'text' },
    { name: 'companion_document', label: 'Buscar acompanhante', type: 'companion-lookup', section: 'Acompanhante' },
    { name: 'companion_name', label: 'Nome', type: 'text' },
    { name: 'companion_birth_date', label: 'Nascimento', type: 'date' },
    { name: 'companion_phone', label: 'Telefone', type: 'text' },
    { name: 'transport_type', label: 'Tipo transporte', type: 'select-static', options: ['0', '1', '2', '3', '4'], section: 'Outros' },
    { name: 'status', label: 'Status', type: 'select-static', options: ['scheduled', 'completed', 'cancelled'] },
    { name: 'notes', label: 'Observacoes', type: 'textarea' },
  ],
};

const LIST_COLUMNS = {
  cities: ['name', 'ibge_code', 'state_code', 'active'],
  drivers: ['name', 'cpf', 'mobile_phone', 'status'],
  vehicles: ['model', 'plate', 'capacity', 'status'],
  patients: ['name', 'cns', 'cpf', 'birth_date', 'mobile_phone'],
  facilities: ['name', 'cnes', 'city_id', 'active'],
  specialties: ['description', 'active'],
  distances: ['origin_city_id', 'destination_city_id', 'distance_km'],
  rides: ['starts_at', 'destination_city_id', 'driver_id', 'vehicle_id', 'status'],
  boardings: ['departure_at', 'patient_id', 'ride_id', 'status'],
};

const DEFAULT_VALUES = {
  active: true,
  in_use: true,
  available: true,
  status: 'active',
  transport_type: '0',
};

const STATUS_TABS = {
  rides: [
    { id: '', label: 'Todos' },
    { id: 'scheduled', label: 'Agendadas' },
    { id: 'completed', label: 'Concluídas' },
    { id: 'cancelled', label: 'Canceladas' },
  ],
  boardings: [
    { id: '', label: 'Todas' },
    { id: 'scheduled', label: 'Agendadas' },
    { id: 'completed', label: 'Concluídas' },
    { id: 'cancelled', label: 'Canceladas' },
  ],
};

const STATUS_BADGE = {
  scheduled: { label: 'Agendada', className: 'bg-amber-50 text-amber-700 ring-amber-200' },
  completed: { label: 'Concluída', className: 'bg-emerald-50 text-emerald-700 ring-emerald-200' },
  cancelled: { label: 'Cancelada', className: 'bg-red-50 text-red-700 ring-red-200' },
  active: { label: 'Ativo', className: 'bg-emerald-50 text-emerald-700 ring-emerald-200' },
  inactive: { label: 'Inativo', className: 'bg-slate-100 text-slate-600 ring-slate-200' },
  maintenance: { label: 'Manutenção', className: 'bg-amber-50 text-amber-700 ring-amber-200' },
};

function getStaticOptionLabel(field, option) {
  if (field.name === 'status') {
    return STATUS_BADGE[option]?.label || option;
  }
  if (field.name === 'gender' || field.name === 'companion_gender') {
    const labels = { male: 'Masculino', female: 'Feminino', other: 'Outro' };
    return labels[option] || option;
  }
  return option;
}

function onlyDigits(value) {
  return String(value || '').replace(/\D/g, '');
}

function friendlyTransportError(message) {
  const text = String(message || '').trim();
  const exact = {
    'Ride capacity is full': 'A corrida está lotada. Não há lugares disponíveis para este embarque.',
    'Patient already has a boarding scheduled for this date': 'Este passageiro já possui um embarque nesta data.',
    'Ride not found': 'Corrida não encontrada.',
    'Vehicle not found': 'Veículo não encontrado.',
    'Driver already has a ride scheduled for this period': 'O motorista já possui corrida neste horário.',
  };
  if (exact[text]) return exact[text];
  if (/Vehicle .+ already has a ride/.test(text)) {
    return 'Este veículo já possui corrida neste horário.';
  }
  if (text.startsWith('Missing required fields')) {
    return 'Preencha os campos obrigatórios.';
  }
  if (/^HTTP 400/.test(text)) {
    return 'Não foi possível salvar. Verifique os dados e tente novamente.';
  }
  return text || 'Erro ao salvar registro';
}

/** CPF: somente 11 dígitos, sem máscara. */
function formatCpfMask(value) {
  return onlyDigits(value).slice(0, 11);
}

function formatCepDigits(value) {
  return onlyDigits(value).slice(0, 8);
}

function composeAddress(parts) {
  const tipoRaw = String(parts?.street_type || '').trim();
  const tipo = /^\d+$/.test(tipoRaw) ? '' : tipoRaw;
  const street = String(parts?.street || '').trim();
  const number = String(parts?.number || '').trim();
  const district = String(parts?.district || '').trim();
  const complement = String(parts?.complement || '').trim();

  let line = street;
  if (tipo && (!line || !line.toUpperCase().startsWith(tipo.toUpperCase()))) {
    line = `${tipo} ${line}`.trim();
  }
  if (number && !line.includes(number)) {
    line = line ? `${line}, ${number}` : number;
  }
  if (district && !line.toUpperCase().includes(district.toUpperCase())) {
    line = line ? `${line} - ${district}` : district;
  }
  if (complement && !line.toUpperCase().includes(complement.toUpperCase())) {
    line = line ? `${line} (${complement})` : complement;
  }
  return line;
}

function normalizeInputValue(value) {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string' && value.includes('T')) return value.slice(0, 16);
  return value;
}

function displayDate(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('pt-BR');
}

function formatOption(record, field) {
  if (!record) return '-';
  const main = record[field.labelField] || record.name || record.description || record.id;
  const secondary = field.secondaryField && record[field.secondaryField] ? ` - ${record[field.secondaryField]}` : '';
  return `${main}${secondary}`;
}

function normalizeSearchText(value) {
  return String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

const SEARCHABLE_PAGE_SIZE = 30;

function BirthCandidatePicker({ candidates, selectedKey, onSelect, getLabel, inputClassName }) {
  const [term, setTerm] = useState('');

  const filtered = useMemo(() => {
    const normalized = normalizeSearchText(term.trim());
    const digits = onlyDigits(term);
    if (!normalized && !digits) return candidates;
    return candidates.filter((candidate) => {
      const record = candidate.record || {};
      const haystack = normalizeSearchText([
        record.name,
        record.mother_name,
        record.cns,
        getLabel(candidate),
      ].join(' '));
      const cpf = onlyDigits(record.cpf);
      return haystack.includes(normalized) || (digits && cpf.includes(digits));
    });
  }, [candidates, term, getLabel]);

  return (
    <div className="mt-3">
      <label className="mb-1.5 block text-sm font-medium text-text" htmlFor="birth-candidate-search">
        Filtrar cidadão
      </label>
      <div className="relative">
        <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
        <input
          id="birth-candidate-search"
          type="text"
          value={term}
          onChange={(event) => setTerm(event.target.value)}
          placeholder="Nome, nome da mãe ou CPF"
          className={`${inputClassName} pl-10`}
          autoComplete="off"
        />
      </div>
      <p className="mt-1.5 text-xs text-muted">
        {filtered.length} de {candidates.length} cidadão(s)
      </p>
      <div className="mt-2 max-h-56 overflow-y-auto rounded-xl border border-border bg-white">
        {filtered.length === 0 ? (
          <p className="px-3 py-3 text-sm text-muted">Nenhum cidadão encontrado com esse filtro.</p>
        ) : (
          filtered.map((candidate) => {
            const selected = candidate.key === selectedKey;
            return (
              <button
                key={candidate.key}
                type="button"
                onClick={() => onSelect(candidate.key)}
                className={`block w-full border-b border-border px-3 py-2.5 text-left text-sm last:border-b-0 ${
                  selected ? 'bg-primary-light font-medium text-primary' : 'text-text hover:bg-slate-50'
                }`}
              >
                {getLabel(candidate)}
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}

function SearchableSelect({ field, value, options, onChange, inputClassName }) {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const [visibleCount, setVisibleCount] = useState(SEARCHABLE_PAGE_SIZE);
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      setTerm('');
      setVisibleCount(SEARCHABLE_PAGE_SIZE);
      setTimeout(() => searchInputRef.current?.focus(), 0);
    }
  }, [open]);

  const filtered = useMemo(() => {
    const normalized = normalizeSearchText(term.trim());
    if (!normalized) return options;
    return options.filter((option) => normalizeSearchText(formatOption(option, field)).includes(normalized));
  }, [options, term, field]);

  const visibleOptions = filtered.slice(0, visibleCount);
  const remaining = filtered.length - visibleOptions.length;
  const selected = options.find((option) => String(option.id) === String(value));

  const selectOption = (option) => {
    onChange(option ? Number(option.id) : '');
    setOpen(false);
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={`${inputClassName} flex items-center justify-between gap-2 text-left`}
      >
        <span className={selected ? 'truncate' : 'truncate text-muted'}>
          {selected ? formatOption(selected, field) : 'Selecione'}
        </span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-muted transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {field.required && (
        <input
          tabIndex={-1}
          value={value || ''}
          required
          onChange={() => {}}
          onFocus={() => setOpen(true)}
          className="pointer-events-none absolute inset-0 h-full w-full opacity-0"
          aria-hidden="true"
        />
      )}
      {open && (
        <div className="absolute z-30 mt-1 w-full overflow-hidden rounded-xl border border-border bg-surface shadow-lg">
          <div className="relative border-b border-border p-2">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
            <input
              ref={searchInputRef}
              value={term}
              onChange={(event) => {
                setTerm(event.target.value);
                setVisibleCount(SEARCHABLE_PAGE_SIZE);
              }}
              placeholder="Buscar..."
              className="w-full rounded-lg border border-border bg-background py-1.5 pl-8 pr-2 text-sm text-text outline-none focus:border-primary"
            />
          </div>
          <ul className="max-h-56 overflow-y-auto py-1 text-sm">
            {!field.required && (
              <li>
                <button
                  type="button"
                  onClick={() => selectOption(null)}
                  className="w-full px-3 py-2 text-left text-muted hover:bg-primary/5"
                >
                  Selecione
                </button>
              </li>
            )}
            {visibleOptions.length === 0 && (
              <li className="px-3 py-2 text-muted">Nenhum resultado encontrado</li>
            )}
            {visibleOptions.map((option) => (
              <li key={option.id}>
                <button
                  type="button"
                  onClick={() => selectOption(option)}
                  className={`w-full px-3 py-2 text-left hover:bg-primary/5 ${String(option.id) === String(value) ? 'bg-primary/10 font-medium text-primary' : 'text-text'}`}
                >
                  {formatOption(option, field)}
                </button>
              </li>
            ))}
            {remaining > 0 && (
              <li className="border-t border-border">
                <button
                  type="button"
                  onClick={() => setVisibleCount((prev) => prev + SEARCHABLE_PAGE_SIZE)}
                  className="w-full px-3 py-2 text-center text-xs font-medium text-primary hover:bg-primary/5"
                >
                  Mostrar mais ({remaining} restantes)
                </button>
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

function getInitials(name) {
  if (!name) return '?';
  return String(name)
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');
}

function StatusBadge({ value }) {
  const config = STATUS_BADGE[value] || {
    label: value || '-',
    className: 'bg-slate-100 text-slate-600 ring-slate-200',
  };

  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset ${config.className}`}>
      {config.label}
    </span>
  );
}

function MetricCard({ icon: Icon, label, value, tone = 'primary' }) {
  const tones = {
    primary: 'bg-primary-light text-primary',
    success: 'bg-emerald-50 text-emerald-600',
    warning: 'bg-amber-50 text-amber-600',
    danger: 'bg-red-50 text-red-600',
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-5 shadow-card">
      <div className="mb-4 flex items-center justify-between">
        <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${tones[tone]}`}>
          <Icon size={18} />
        </div>
      </div>
      <div className="text-3xl font-semibold tracking-tight text-text">{value}</div>
      <div className="mt-1 text-sm text-muted">{label}</div>
    </div>
  );
}

function ActionMenu({ onEdit, onDelete }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!onEdit && !onDelete) return null;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-white text-muted transition hover:bg-slate-50 hover:text-text"
        aria-label="Ações"
      >
        <MoreHorizontal size={16} />
      </button>
      {open && (
        <div className="absolute right-0 z-20 mt-2 w-40 overflow-hidden rounded-xl border border-border bg-white py-1 shadow-lg">
          {onEdit && (
            <button
              type="button"
              onClick={() => { setOpen(false); onEdit(); }}
              className="flex w-full items-center gap-2 px-3 py-2 text-sm text-text hover:bg-slate-50"
            >
              <Pencil size={14} /> Editar
            </button>
          )}
          {onDelete && (
            <button
              type="button"
              onClick={() => { setOpen(false); onDelete(); }}
              className="flex w-full items-center gap-2 px-3 py-2 text-sm text-danger hover:bg-red-50"
            >
              <Trash2 size={14} /> Excluir
            </button>
          )}
        </div>
      )}
    </div>
  );
}

const Transport = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const pathResource = resourceFromPath(location.pathname);
  const storedTab = sessionStorage.getItem(LAST_TRANSPORT_TAB_KEY);
  const [activeResource, setActiveResource] = useState(
    pathResource || (RESOURCE_ORDER.includes(storedTab) ? storedTab : 'rides')
  );
  const [records, setRecords] = useState([]);
  const [lookups, setLookups] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [formData, setFormData] = useState({});
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterDate, setFilterDate] = useState('');
  const [filterBirthDate, setFilterBirthDate] = useState('');
  const [selectedBoardingPatient, setSelectedBoardingPatient] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 0 });
  const [loadingMore, setLoadingMore] = useState(false);
  const [metrics, setMetrics] = useState({ today: 0, scheduled: 0, completed: 0, cancelled: 0 });
  // Guarda o nome do campo em busca ('cpf' ou 'companion_document') para
  // mostrar o indicador apenas no input correspondente
  const [lookingUpField, setLookingUpField] = useState(null);
  const lastEsusCpfRef = useRef('');
  const lastCompanionCpfRef = useRef('');
  const [lockedRideId, setLockedRideId] = useState(null);
  const [formError, setFormError] = useState('');
  const [formResource, setFormResource] = useState(null);
  const [birthSearchDate, setBirthSearchDate] = useState('');
  const [birthCandidates, setBirthCandidates] = useState([]);
  const [birthSearchLoading, setBirthSearchLoading] = useState(false);
  const [selectedBirthKey, setSelectedBirthKey] = useState('');
  const [showBirthLookup, setShowBirthLookup] = useState(false);
  const [completingRideId, setCompletingRideId] = useState(null);
  const [printingRideId, setPrintingRideId] = useState(null);
  const [rideToComplete, setRideToComplete] = useState(null);

  const fields = useMemo(() => FIELD_CONFIG[activeResource] || [], [activeResource]);
  const formFields = useMemo(
    () => FIELD_CONFIG[formResource || activeResource] || [],
    [formResource, activeResource]
  );
  const columns = useMemo(() => LIST_COLUMNS[activeResource] || [], [activeResource]);
  const statusTabs = STATUS_TABS[activeResource] || null;
  const visibleResources = useMemo(
    () => RESOURCE_ORDER.filter((resource) => getTransportPermission(user, resource).canView),
    [user]
  );
  const resourcePermission = useMemo(
    () => getTransportPermission(user, activeResource),
    [user, activeResource]
  );
  const ridePermission = useMemo(() => getTransportPermission(user, 'rides'), [user]);
  const boardingPermission = useMemo(() => getTransportPermission(user, 'boardings'), [user]);

  useEffect(() => {
    const allowed = visibleResources.length ? visibleResources : RESOURCE_ORDER;

    if (pathResource) {
      if (!allowed.includes(pathResource) && allowed[0]) {
        navigate(`/transport/${allowed[0]}`, { replace: true });
        return;
      }
      setActiveResource(pathResource);
      sessionStorage.setItem(LAST_TRANSPORT_TAB_KEY, pathResource);
      return;
    }

    const stored = sessionStorage.getItem(LAST_TRANSPORT_TAB_KEY);
    const fallback = allowed.includes(stored) ? stored : allowed[0] || 'rides';
    navigate(`/transport/${fallback}`, { replace: true });
  }, [pathResource, navigate, visibleResources]);

  const loadRecords = useCallback(async (page = 1, { append = false } = {}) => {
    try {
      if (append) setLoadingMore(true);
      else setLoading(true);

      const limit = activeResource === 'boardings' ? 12 : pagination.limit;
      const params = {
        page,
        limit,
        search,
      };
      if (filterStatus) params.status = filterStatus;
      if (filterDate) params.date = filterDate;
      if (activeResource === 'patients' && filterBirthDate) params.birth_date = filterBirthDate;

      if (activeResource === 'boardings') {
        const response = await apiService.listTransport('rides', {
          ...params,
          include_boardings: true,
        });

        if (response.success) {
          let rides = response.data?.results || [];
          const nested = rides.some((ride) => Array.isArray(ride.boardings));
          if (!nested && rides.length > 0) {
            const boardingResponses = await Promise.all(
              rides.map((ride) => apiService.listTransport('boardings', { ride_id: ride.id, limit: 500 }))
            );
            rides = rides.map((ride, index) => ({
              ...ride,
              boardings: boardingResponses[index]?.data?.results || [],
            }));
          }
          setRecords((prev) => {
            if (!append) return rides;
            const existing = new Set(prev.map((item) => item.id));
            return [...prev, ...rides.filter((ride) => !existing.has(ride.id))];
          });
          setPagination(response.data?.pagination || { page, limit, total: 0, totalPages: 0 });
        }
        return;
      }

      const response = await apiService.listTransport(activeResource, params);

      if (response.success) {
        setRecords(response.data?.results || []);
        setPagination(response.data?.pagination || { page, limit, total: 0, totalPages: 0 });
      }
    } catch (error) {
      console.error('Erro ao carregar transporte:', error);
      toast.error('Erro ao carregar dados do modulo de transporte');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [activeResource, filterBirthDate, filterDate, filterStatus, pagination.limit, search]);

  const loadMetrics = useCallback(async () => {
    if (activeResource !== 'rides') return;

    try {
      const today = new Date().toISOString().split('T')[0];
      const [todayRes, scheduledRes, completedRes, cancelledRes] = await Promise.all([
        apiService.listTransport('rides', { limit: 1, date: today }),
        apiService.listTransport('rides', { limit: 1, status: 'scheduled' }),
        apiService.listTransport('rides', { limit: 1, status: 'completed' }),
        apiService.listTransport('rides', { limit: 1, status: 'cancelled' }),
      ]);

      setMetrics({
        today: todayRes.data?.pagination?.total || 0,
        scheduled: scheduledRes.data?.pagination?.total || 0,
        completed: completedRes.data?.pagination?.total || 0,
        cancelled: cancelledRes.data?.pagination?.total || 0,
      });
    } catch (error) {
      console.error('Erro ao carregar indicadores:', error);
    }
  }, [activeResource]);

  const loadLookup = useCallback(async (resource) => {
    if (lookups[resource]) return;

    try {
      // Percorre todas as páginas para trazer o cadastro completo (ex.: 5.600+ cidades)
      const all = [];
      let page = 1;
      let hasNext = true;

      while (hasNext && page <= 20) {
        const response = await apiService.listTransport(resource, { limit: 1000, page });
        if (!response.success) break;

        all.push(...(response.data?.results || []));
        hasNext = Boolean(response.data?.pagination?.hasNext);
        page += 1;
      }

      setLookups((prev) => ({
        ...prev,
        [resource]: all,
      }));
    } catch (error) {
      console.error(`Erro ao carregar lookup ${resource}:`, error);
    }
  }, [lookups]);

  useEffect(() => {
    const timer = setTimeout(() => {
      loadRecords(1);
    }, 350);
    return () => clearTimeout(timer);
  }, [activeResource, filterBirthDate, filterDate, filterStatus, loadRecords, search]);

  useEffect(() => {
    loadMetrics();
  }, [activeResource, loadMetrics, records]);

  useEffect(() => {
    const resources = [...fields, ...formFields]
      .filter((field) => field.type === 'select' && field.resource)
      .map((field) => field.resource);
    if (activeResource === 'boardings') {
      resources.push('drivers', 'vehicles', 'cities', 'patients', 'facilities', 'specialties');
    }
    [...new Set(resources)].forEach((resource) => loadLookup(resource));
  }, [activeResource, fields, formFields, loadLookup]);

  const clearFilters = () => {
    setSearch('');
    setFilterStatus('');
    setFilterDate('');
    setFilterBirthDate('');
  };

  const applyBoardingPatient = (patient) => {
    if (!patient?.id) return;
    setSelectedBoardingPatient(patient);
    setLookups((prev) => {
      const patients = prev.patients || [];
      if (patients.some((item) => Number(item.id) === Number(patient.id))) return prev;
      return { ...prev, patients: [patient, ...patients] };
    });
    setFormData((prev) => ({
      ...prev,
      patient_id: Number(patient.id),
      patient_phone: patient.mobile_phone || prev.patient_phone || '',
      companion_name: patient.companion_name || '',
      companion_document: patient.companion_document ? formatCpfMask(patient.companion_document) : '',
      companion_phone: patient.companion_phone || '',
      companion_birth_date: patient.companion_birth_date
        ? String(patient.companion_birth_date).slice(0, 10)
        : '',
    }));
  };

  const openCreateForm = (overrides = {}) => {
    const initialData = fields.reduce((acc, field) => {
      if (overrides[field.name] !== undefined) {
        acc[field.name] = overrides[field.name];
      } else if (DEFAULT_VALUES[field.name] !== undefined) {
        acc[field.name] = DEFAULT_VALUES[field.name];
      } else if (field.type === 'checkbox') {
        acc[field.name] = false;
      } else {
        acc[field.name] = '';
      }
      return acc;
    }, {});

    lastEsusCpfRef.current = '';
    lastCompanionCpfRef.current = '';
    setBirthSearchDate('');
    setBirthCandidates([]);
    setSelectedBirthKey('');
    setBirthSearchLoading(false);
    setShowBirthLookup(activeResource === 'patients');
    setEditingRecord(null);
    setSelectedBoardingPatient(null);
    setLockedRideId(overrides.ride_id || null);
    setFormResource(activeResource);
    setFormError('');
    setFormData(initialData);
    setShowForm(true);
  };

  const openCreateFormForRide = (ride) => {
    openCreateForm({
      ride_id: ride.id,
      departure_at: normalizeInputValue(ride.starts_at),
      destination_city_id: ride.destination_city_id,
      transport_type: ride.transport_type ?? '0',
      status: 'scheduled',
    });
  };

  const fillFormFromRecord = (record, resource) => {
    const resourceFields = FIELD_CONFIG[resource] || [];
    const data = resourceFields.reduce((acc, field) => {
      const value = normalizeInputValue(record[field.name]);
      if (field.type === 'cpf') {
        acc[field.name] = formatCpfMask(value);
      } else if (field.type === 'cep') {
        acc[field.name] = formatCepDigits(value);
      } else if (field.type === 'hidden' && field.name === 'pickup_at_home') {
        acc[field.name] = record.pickup_at_home === true;
      } else {
        acc[field.name] = value;
      }
      return acc;
    }, {});

    if (resource === 'patients') {
      const composed = composeAddress(record);
      if (composed) data.street = composed;
    }

    return data;
  };

  const openEditForm = (record) => {
    const data = fillFormFromRecord(record, activeResource);

    lastEsusCpfRef.current = onlyDigits(data.cpf);
    lastCompanionCpfRef.current = onlyDigits(data.companion_document);
    setEditingRecord(record);
    const cachedPatient = activeResource === 'boardings'
      ? (lookups.patients || []).find((item) => Number(item.id) === Number(record.patient_id)) || null
      : null;
    setSelectedBoardingPatient(cachedPatient);
    setFormResource(activeResource);
    setLockedRideId(record.ride_id || null);
    setFormError('');
    setFormData(data);
    setShowBirthLookup(false);
    setShowForm(true);

    if (activeResource === 'boardings' && record.patient_id && !cachedPatient) {
      apiService.getTransport('patients', record.patient_id)
        .then((response) => {
          if (response.success && response.data) {
            setSelectedBoardingPatient(response.data);
            setLookups((prev) => {
              const patients = prev.patients || [];
              if (patients.some((item) => Number(item.id) === Number(response.data.id))) return prev;
              return { ...prev, patients: [response.data, ...patients] };
            });
          }
        })
        .catch(() => {});
    }
  };

  const openEditRideForm = (ride) => {
    ['cities', 'drivers', 'vehicles'].forEach((resource) => loadLookup(resource));
    lastEsusCpfRef.current = '';
    lastCompanionCpfRef.current = '';
    setEditingRecord(ride);
    setFormResource('rides');
    setLockedRideId(null);
    setFormError('');
    setShowBirthLookup(false);
    setFormData(fillFormFromRecord(ride, 'rides'));
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingRecord(null);
    setFormData({});
    setLookingUpField(null);
    setLockedRideId(null);
    setFormResource(null);
    setFormError('');
    setSelectedBoardingPatient(null);
    lastEsusCpfRef.current = '';
    lastCompanionCpfRef.current = '';
    setBirthSearchDate('');
    setBirthCandidates([]);
    setSelectedBirthKey('');
    setBirthSearchLoading(false);
    setShowBirthLookup(false);
  };

  const handleChange = (field, value) => {
    const nextValue = field.type === 'number' && value !== '' ? Number(value) : value;
    setFormError('');

    if ((formResource || activeResource) === 'boardings' && field.name === 'patient_id' && nextValue) {
      const patient = (lookups.patients || []).find((item) => Number(item.id) === Number(nextValue));
      if (patient) {
        applyBoardingPatient(patient);
        return;
      }
    }

    setFormData((prev) => ({
      ...prev,
      [field.name]: nextValue,
    }));
  };

  const togglePickupAtHome = () => {
    setFormData((prev) => {
      const marked = !prev.pickup_at_home;
      const address = String(prev.street || '').trim();
      const currentPoint = String(prev.boarding_point || '').trim();
      return {
        ...prev,
        pickup_at_home: marked,
        boarding_point: marked && !currentPoint && address ? address : prev.boarding_point,
      };
    });
  };

  const registerCityOption = (cityId, cityName, ibgeCode) => {
    if (!cityId) return;

    setLookups((prev) => {
      const cities = prev.cities || [];
      const exists = cities.some((city) => Number(city.id) === Number(cityId));
      if (exists) return prev;

      return {
        ...prev,
        cities: [
          {
            id: Number(cityId),
            name: cityName || `Cidade ${cityId}`,
            ibge_code: ibgeCode || null,
          },
          ...cities,
        ],
      };
    });

    if (!lookups.cities) {
      loadLookup('cities');
    }
  };

  const applyLookupData = (data, { companionOnly = false } = {}) => {
    setFormData((prev) => {
      const next = { ...prev };

      if (companionOnly) {
        Object.entries(data).forEach(([key, value]) => {
          if (value === null || value === undefined) return;
          if (['source', 'companion_city_name'].includes(key)) return;
          next[key] = value;
        });

        if (data.companion_document) next.companion_document = formatCpfMask(data.companion_document);
        if (data.companion_zip_code) next.companion_zip_code = formatCepDigits(data.companion_zip_code);
        if (data.companion_street) next.companion_street = data.companion_street;
        if (data.companion_city_id !== undefined && data.companion_city_id !== null && data.companion_city_id !== '') {
          next.companion_city_id = Number(data.companion_city_id);
        }

        return next;
      }

      Object.entries(data).forEach(([key, value]) => {
        if (value === null || value === undefined) return;
        if (['source', 'existing_id', 'esus_id', 'city_name', 'ibge_code'].includes(key)) return;
        next[key] = value;
      });

      if (data.cpf) next.cpf = formatCpfMask(data.cpf);
      if (data.companion_document) next.companion_document = formatCpfMask(data.companion_document);
      if (data.zip_code) next.zip_code = formatCepDigits(data.zip_code);
      if (data.street) next.street = data.street;
      if (data.companion_zip_code) next.companion_zip_code = formatCepDigits(data.companion_zip_code);
      if (data.companion_street) next.companion_street = data.companion_street;
      if (data.city_id !== undefined && data.city_id !== null && data.city_id !== '') {
        next.city_id = Number(data.city_id);
      }
      if (data.companion_city_id !== undefined && data.companion_city_id !== null && data.companion_city_id !== '') {
        next.companion_city_id = Number(data.companion_city_id);
      }
      if (data.active !== undefined) next.active = data.active;
      if (data.pickup_at_home !== undefined) next.pickup_at_home = data.pickup_at_home === true;

      const composed = composeAddress(next);
      if (composed) next.street = composed;

      return next;
    });

    if (!companionOnly && data.existing_id) {
      setEditingRecord({ id: data.existing_id });
    }
  };

  const lookupEsusByCpf = async (cpfMasked, { companionOnly = false } = {}) => {
    const digits = onlyDigits(cpfMasked);
    const lastRef = companionOnly ? lastCompanionCpfRef : lastEsusCpfRef;

    if (digits.length !== 11 || lastRef.current === digits) {
      return;
    }

    if (!companionOnly && editingRecord) {
      return;
    }

    lastRef.current = digits;

    try {
      setLookingUpField(companionOnly ? 'companion_document' : 'cpf');
      const response = await apiService.lookupEsusCidadaoByCpf(
        digits,
        companionOnly ? 'companion' : 'patient'
      );

      if (response.success && response.data) {
        applyLookupData(response.data, { companionOnly });

        if (companionOnly) {
          toast.success('Dados do acompanhante encontrados');
          registerCityOption(response.data.companion_city_id, response.data.companion_city_name);
        } else if (response.data.source === 'local') {
          toast.success('Passageiro encontrado no cadastro local');
          registerCityOption(response.data.city_id, response.data.city_name, response.data.ibge_code);
        } else {
          toast.success('Dados encontrados no e-SUS');
          if (!response.data.city_id && response.data.city_name) {
            toast.error(`Cidade "${response.data.city_name}" não está cadastrada em Cidades`);
          }
          registerCityOption(response.data.city_id, response.data.city_name, response.data.ibge_code);
        }
      }
    } catch (error) {
      console.error('Erro ao buscar CPF no e-SUS:', error);
      toast.error(error.message || 'Usuário não cadastrado no e-SUS do município');
    } finally {
      setLookingUpField(null);
    }
  };

  const searchPatientsByBirthDate = async () => {
    if (!birthSearchDate) {
      toast.error('Informe a data de nascimento');
      return;
    }

    try {
      setBirthSearchLoading(true);
      setBirthCandidates([]);
      setSelectedBirthKey('');
      const response = await apiService.lookupPatientsByBirthDate(birthSearchDate);
      const candidates = response.data?.candidates || [];
      setBirthCandidates(candidates);

      if (!candidates.length) {
        toast('Nenhum cidadão encontrado para esta data de nascimento.', { icon: 'ℹ️' });
        return;
      }

      if (response.data?.truncated) {
        toast('A busca retornou muitos registros. Refine a data se a pessoa não aparecer.', { icon: 'ℹ️' });
      } else {
        toast.success(`${candidates.length} cidadão(s) encontrado(s)`);
      }
    } catch (error) {
      console.error('Erro ao buscar cidadãos por nascimento:', error);
      toast.error(error.message || 'Não foi possível buscar no cadastro do município');
    } finally {
      setBirthSearchLoading(false);
    }
  };

  const handleSelectBirthCandidate = (key) => {
    setSelectedBirthKey(key);
    const candidate = birthCandidates.find((item) => item.key === key);
    if (!candidate?.record) return;

    applyLookupData(candidate.record, { companionOnly: false });
    registerCityOption(
      candidate.record.city_id,
      candidate.record.city_name,
      candidate.record.ibge_code
    );

    if (candidate.source === 'local') {
      toast.success('Passageiro já cadastrado. Os dados foram carregados para edição.');
    } else {
      toast.success('Dados do cidadão preenchidos a partir do e-SUS');
      if (!candidate.record.city_id && candidate.record.city_name) {
        toast.error(`Cidade "${candidate.record.city_name}" não está cadastrada em Cidades`);
      }
    }
  };

  const birthCandidateLabel = (candidate) => {
    const record = candidate.record || {};
    const cpf = formatCpfMask(record.cpf);
    const mother = record.mother_name ? ` — mãe: ${record.mother_name}` : '';
    const document = cpf ? ` — CPF ${cpf}` : (record.cns ? ` — CNS ${record.cns}` : '');
    const origin = candidate.source === 'local' ? ' (já cadastrado)' : '';
    return `${record.name || 'Sem nome'}${mother}${document}${origin}`;
  };

  const handleCpfChange = (field, rawValue) => {
    const masked = formatCpfMask(rawValue);
    handleChange(field, masked);

    const digits = onlyDigits(masked);
    const companionOnly = field.lookupMode === 'companion' || field.name === 'companion_document';

    if (digits.length < 11) {
      if (companionOnly) lastCompanionCpfRef.current = '';
      else lastEsusCpfRef.current = '';
      return;
    }

    const resource = formResource || activeResource;
    if (companionOnly) {
      if (resource !== 'patients' && resource !== 'boardings') return;
      lookupEsusByCpf(masked, { companionOnly: true });
      return;
    }

    if (resource !== 'patients') return;

    if (!editingRecord) {
      lookupEsusByCpf(masked, { companionOnly: false });
    }
  };

  const cleanPayload = () => Object.entries(formData).reduce((acc, [key, value]) => {
    acc[key] = value === '' ? null : value;
    return acc;
  }, {});

  const handleSubmit = async (event) => {
    event.preventDefault();

    try {
      setSaving(true);
      const resource = formResource || activeResource;
      const payload = cleanPayload();
      const response = editingRecord
        ? await apiService.updateTransport(resource, editingRecord.id, payload)
        : await apiService.createTransport(resource, payload);

      if (response.success) {
        toast.success(editingRecord ? 'Registro atualizado com sucesso' : 'Registro criado com sucesso');
        closeForm();
        if (activeResource === 'boardings') {
          loadRecords(1);
        } else {
          loadRecords(pagination.page || 1);
        }
        loadMetrics();
        setLookups((prev) => ({ ...prev, [resource]: null, [activeResource]: null }));
      }
    } catch (error) {
      console.error('Erro ao salvar transporte:', error);
      const message = friendlyTransportError(error.message);
      setFormError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (record) => {
    if (!window.confirm('Tem certeza que deseja excluir este registro?')) return;

    try {
      setLoading(true);
      const response = await apiService.deleteTransport(activeResource, record.id);
      if (response.success) {
        toast.success('Registro excluido com sucesso');
        if (activeResource === 'boardings') {
          loadRecords(1);
        } else {
          loadRecords(pagination.page || 1);
        }
        loadMetrics();
      }
    } catch (error) {
      console.error('Erro ao excluir transporte:', error);
      toast.error(error.message || 'Erro ao excluir registro');
    } finally {
      setLoading(false);
    }
  };

  const requestCompleteRide = (ride) => {
    if (ride.status === 'completed' || ride.status === 'cancelled') return;
    setRideToComplete(ride);
  };

  const handlePrintRoteiro = async (ride) => {
    if (!ride?.id) return;
    try {
      setPrintingRideId(ride.id);
      const toastId = toast.loading('Gerando relação de passageiros...');
      const result = await apiService.printRideRoteiro(ride.id);
      if (result.success && result.data?.arquivoBase64) {
        downloadPdfBase64(result.data.arquivoBase64, result.data.nomeArquivo);
        toast.success('Roteiro gerado com sucesso', { id: toastId });
      } else {
        toast.error(result.error || 'Arquivo não disponível', { id: toastId });
      }
    } catch (error) {
      toast.error(error.message || 'Erro ao gerar roteiro');
    } finally {
      setPrintingRideId(null);
    }
  };

  const closeCompleteRideModal = () => {
    if (completingRideId) return;
    setRideToComplete(null);
  };

  const handleCompleteRide = async () => {
    const ride = rideToComplete;
    if (!ride || ride.status === 'completed' || ride.status === 'cancelled') return;

    try {
      setCompletingRideId(ride.id);
      const response = await apiService.updateTransport('rides', ride.id, { status: 'completed' });
      if (!response.success) {
        throw new Error(response.error || 'Não foi possível concluir a corrida');
      }

      const pending = (ride.boardings || []).filter(
        (item) => item.status === 'scheduled' || item.status === 'active'
      );
      await Promise.all(
        pending.map((boarding) => apiService.updateTransport('boardings', boarding.id, { status: 'completed' }))
      );

      toast.success('Corrida concluída');
      setRideToComplete(null);
      loadRecords(1);
      loadMetrics();
    } catch (error) {
      console.error('Erro ao concluir corrida:', error);
      toast.error(friendlyTransportError(error.message) || 'Erro ao concluir corrida');
    } finally {
      setCompletingRideId(null);
    }
  };

  const inputClassName = 'w-full rounded-xl border border-border bg-white px-3 py-2.5 text-sm text-text outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20';

  const renderField = (field) => {
    const value = formData[field.name];

    if (field.type === 'companion-lookup' && (formResource || activeResource) === 'boardings') {
      return (
        <CompanionLookupSelect
          companionDocument={formData.companion_document}
          companionName={formData.companion_name}
          companionPhone={formData.companion_phone}
          companionBirthDate={formData.companion_birth_date}
          inputClassName={inputClassName}
          onSelect={(data) => {
            lastCompanionCpfRef.current = onlyDigits(data.companion_document);
            setFormData((prev) => ({
              ...prev,
              companion_document: data.companion_document || '',
              companion_name: data.companion_name || '',
              companion_phone: data.companion_phone || '',
              companion_birth_date: data.companion_birth_date || '',
            }));
          }}
          onClear={() => {
            lastCompanionCpfRef.current = '';
            setFormData((prev) => ({
              ...prev,
              companion_document: '',
              companion_name: '',
              companion_phone: '',
              companion_birth_date: '',
            }));
          }}
        />
      );
    }

    if (field.type === 'cpf') {
      const isCompanionLookup = field.lookupMode === 'companion' || field.name === 'companion_document';
      const isLookingUpThisField = lookingUpField === field.name;
      return (
        <div className="relative">
          <input
            type="text"
            name={field.name}
            value={value || ''}
            required={field.required}
            inputMode="numeric"
            autoComplete="off"
            placeholder="Somente 11 digitos"
            maxLength={11}
            disabled={isLookingUpThisField}
            onChange={(event) => handleCpfChange(field, event.target.value)}
            className={inputClassName}
          />
          {isLookingUpThisField && (
            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted">
              {isCompanionLookup ? 'Buscando acompanhante...' : 'Buscando e-SUS...'}
            </span>
          )}
        </div>
      );
    }

    if (field.type === 'cep') {
      return (
        <input
          type="text"
          name={field.name}
          value={value || ''}
          required={field.required}
          inputMode="numeric"
          autoComplete="off"
          placeholder="00000000"
          maxLength={8}
          onChange={(event) => handleChange(field, formatCepDigits(event.target.value))}
          className={inputClassName}
        />
      );
    }

    if (field.type === 'textarea') {
      return (
        <textarea
          name={field.name}
          value={value || ''}
          required={field.required}
          onChange={(event) => handleChange(field, event.target.value)}
          className={`${inputClassName} min-h-[96px] resize-y`}
        />
      );
    }

    if (field.type === 'checkbox') {
      return (
        <label className="flex items-center gap-2 text-sm text-text">
          <input
            type="checkbox"
            checked={Boolean(value)}
            onChange={(event) => handleChange(field, event.target.checked)}
            className="h-4 w-4 rounded border-border text-primary focus:ring-primary/30"
          />
          <span>{field.label}</span>
        </label>
      );
    }

    if (field.type === 'select-static') {
      return (
        <select
          name={field.name}
          value={value ?? ''}
          required={field.required}
          onChange={(event) => handleChange(field, event.target.value)}
          className={inputClassName}
        >
          <option value="">Selecione</option>
          {field.options.map((option) => (
            <option key={option} value={option}>{getStaticOptionLabel(field, option)}</option>
          ))}
        </select>
      );
    }

    if (field.type === 'select') {
      if (field.resource === 'patients' && (formResource || activeResource) === 'boardings') {
        const selectedPatient = selectedBoardingPatient
          || (lookups.patients || []).find((item) => Number(item.id) === Number(value))
          || null;
        return (
          <PatientLookupSelect
            value={value}
            selectedPatient={selectedPatient}
            inputClassName={inputClassName}
            required={field.required}
            onSelect={applyBoardingPatient}
            onClear={() => {
              setSelectedBoardingPatient(null);
              setFormData((prev) => ({
                ...prev,
                patient_id: '',
                patient_phone: '',
                companion_name: '',
                companion_document: '',
                companion_phone: '',
                companion_birth_date: '',
              }));
            }}
          />
        );
      }

      const options = lookups[field.resource] || [];
      if (field.resource === 'cities' || options.length > 50) {
        return (
          <SearchableSelect
            field={field}
            value={value}
            options={options}
            onChange={(nextValue) => handleChange(field, nextValue)}
            inputClassName={inputClassName}
          />
        );
      }
      return (
        <select
          name={field.name}
          value={value || ''}
          required={field.required}
          onChange={(event) => handleChange(field, event.target.value ? Number(event.target.value) : '')}
          className={inputClassName}
        >
          <option value="">Selecione</option>
          {options.map((option) => (
            <option key={option.id} value={option.id}>{formatOption(option, field)}</option>
          ))}
        </select>
      );
    }

    if (field.homePickup) {
      const marked = Boolean(formData.pickup_at_home);
      return (
        <div>
          <div className="relative">
            <input
              type="text"
              name={field.name}
              value={value || ''}
              required={field.required}
              onChange={(event) => handleChange(field, event.target.value)}
              className={`${inputClassName} pr-12`}
            />
            <button
              type="button"
              onClick={togglePickupAtHome}
              aria-pressed={marked}
              title={marked
                ? 'Busca em casa marcada. Clique para desmarcar.'
                : 'Marcar este endereço como local de embarque (busca em casa)'}
              className={`absolute right-1.5 top-1/2 inline-flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-lg transition ${
                marked ? 'bg-primary text-white' : 'text-slate-400 hover:bg-slate-100 hover:text-primary'
              }`}
            >
              <Home size={16} fill={marked ? 'currentColor' : 'none'} />
            </button>
          </div>
          {marked && (
            <p className="mt-1 text-xs font-medium text-primary">Busca em casa</p>
          )}
        </div>
      );
    }

    return (
      <input
        type={field.type}
        name={field.name}
        value={value || ''}
        required={field.required}
        onChange={(event) => handleChange(field, event.target.value)}
        className={inputClassName}
      />
    );
  };

  const displayCell = (record, column) => {
    const value = record[column];
    const field = fields.find((item) => item.name === column);

    if (column === 'status') {
      return <StatusBadge value={value} />;
    }

    if (column === 'name' && activeResource === 'patients' && record.pickup_at_home) {
      return (
        <span className="inline-flex items-center gap-2">
          <Home size={14} className="shrink-0 text-primary" aria-label="Busca em casa" />
          <span>{value}</span>
        </span>
      );
    }

    if (column === 'cpf') {
      return formatCpfMask(value) || '-';
    }

    if (column === 'birth_date') {
      const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (!match) return value || '-';
      return `${match[3]}/${match[2]}/${match[1]}`;
    }

    if (field?.type === 'select') {
      const related = (lookups[field.resource] || []).find((item) => Number(item.id) === Number(value));
      if (column === 'driver_id' && related?.name) {
        return (
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-light text-xs font-semibold text-primary">
              {getInitials(related.name)}
            </div>
            <span>{related.name}</span>
          </div>
        );
      }
      if (column === 'vehicle_id' && related) {
        return (
          <div>
            <div className="font-medium text-text">{related.model || '-'}</div>
            <div className="text-xs text-muted">{related.plate || '-'}</div>
          </div>
        );
      }
      return formatOption(related, field);
    }

    if (column.endsWith('_at') || column.endsWith('_date')) {
      return displayDate(value);
    }

    if (typeof value === 'boolean') {
      return value ? 'Sim' : 'Não';
    }

    return value || '-';
  };

  const pageStart = pagination.total ? ((pagination.page - 1) * pagination.limit) + 1 : 0;
  const pageEnd = Math.min(pagination.page * pagination.limit, pagination.total || 0);
  const lockedRide = lockedRideId
    ? records.find((item) => Number(item.id) === Number(lockedRideId))
      || (lookups.rides || []).find((item) => Number(item.id) === Number(lockedRideId))
    : null;
  const lockedRideOrigin = lockedRide
    ? (lookups.cities || []).find((city) => Number(city.id) === Number(lockedRide.origin_city_id))?.name
    : '';
  const lockedRideDestination = lockedRide
    ? (lookups.cities || []).find((city) => Number(city.id) === Number(lockedRide.destination_city_id))?.name
    : '';

  return (
    <div className="min-h-screen bg-surface">
      <Toaster position="top-right" />

      <div className="border-b border-border bg-white px-6 py-5 lg:px-8">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-text">Transporte</h1>
            <p className="mt-1 text-sm text-muted">
              Gerencie corridas, embarques e cadastros auxiliares.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden items-center gap-3 rounded-2xl border border-border bg-slate-50 px-3 py-2 sm:flex">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary text-sm font-semibold text-white">
                {getInitials(user?.name || user?.username)}
              </div>
              <div>
                <div className="text-sm font-medium text-text">{user?.name || user?.username}</div>
                <div className="text-xs text-muted">{user?.role || 'admin'}</div>
              </div>
            </div>
            {activeResource !== 'boardings' && resourcePermission.canCreate && (
              <button
                type="button"
                onClick={() => openCreateForm()}
                className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-blue-700"
              >
                <Plus size={16} />
                Novo registro
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="px-6 py-6 lg:px-8">
        {activeResource === 'rides' && (
          <div className="mb-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <MetricCard icon={Car} label="Corridas hoje" value={metrics.today} tone="primary" />
            <MetricCard icon={Route} label="Agendadas" value={metrics.scheduled} tone="warning" />
            <MetricCard icon={CircleDot} label="Concluídas" value={metrics.completed} tone="success" />
            <MetricCard icon={Bus} label="Canceladas" value={metrics.cancelled} tone="danger" />
          </div>
        )}

        <div className="mb-4 flex flex-wrap gap-2">
          {(visibleResources.length ? visibleResources : RESOURCE_ORDER).map((resource) => (
            <button
              key={resource}
              type="button"
              onClick={() => {
                sessionStorage.setItem(LAST_TRANSPORT_TAB_KEY, resource);
                navigate(`/transport/${resource}`);
                setSearch('');
                setFilterStatus('');
                setFilterDate('');
                setFilterBirthDate('');
              }}
              className={`rounded-full px-4 py-2 text-sm font-medium transition ${
                activeResource === resource
                  ? 'bg-primary text-white shadow-sm'
                  : 'border border-border bg-white text-muted hover:bg-slate-50 hover:text-text'
              }`}
            >
              {RESOURCE_LABELS[resource]}
            </button>
          ))}
        </div>

        <section className="overflow-hidden rounded-2xl border border-border bg-card shadow-card">
          <div className="border-b border-border px-5 py-4">
            <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
              <div>
                <h2 className="text-lg font-semibold text-text">
                  {activeResource === 'boardings' ? 'Embarques por corrida' : RESOURCE_LABELS[activeResource]}
                </h2>
                <p className="text-sm text-muted">
                  {activeResource === 'boardings'
                    ? `${records.length} de ${pagination.total || 0} corridas`
                    : `${pagination.total || 0} registros`}
                </p>
              </div>

              <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
                <div className="relative min-w-[260px]">
                  <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
                  <input
                    type="text"
                    placeholder={
                      activeResource === 'boardings'
                        ? 'Passageiro, nascimento (dd/mm/aaaa), motorista, placa...'
                        : activeResource === 'patients'
                          ? 'Pesquisar nome, CPF, CNS, mãe ou nascimento...'
                          : 'Pesquisar motorista, veículo ou destino...'
                    }
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    className="w-full rounded-xl border border-border bg-white py-2.5 pl-10 pr-3 text-sm outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20"
                  />
                </div>

                {statusTabs && (
                  <select
                    value={filterStatus}
                    onChange={(event) => setFilterStatus(event.target.value)}
                    className="rounded-xl border border-border bg-white px-3 py-2.5 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                  >
                    {statusTabs.map((tab) => (
                      <option key={tab.id || 'all'} value={tab.id}>{tab.label}</option>
                    ))}
                  </select>
                )}

                {(activeResource === 'rides' || activeResource === 'boardings') && (
                  <input
                    type="date"
                    title="Data da corrida"
                    aria-label="Data da corrida"
                    value={filterDate}
                    onChange={(event) => setFilterDate(event.target.value)}
                    className="rounded-xl border border-border bg-white px-3 py-2.5 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                  />
                )}

                {activeResource === 'patients' && (
                  <input
                    type="date"
                    title="Data de nascimento"
                    aria-label="Data de nascimento"
                    value={filterBirthDate}
                    onChange={(event) => setFilterBirthDate(event.target.value)}
                    className="rounded-xl border border-border bg-white px-3 py-2.5 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                  />
                )}

                <button
                  type="button"
                  onClick={clearFilters}
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-border bg-white px-3 py-2.5 text-sm text-muted transition hover:bg-slate-50 hover:text-text"
                >
                  <FilterX size={16} />
                  Limpar filtros
                </button>
              </div>
            </div>

            {statusTabs && (
              <div className="mt-4 flex flex-wrap gap-2">
                {statusTabs.map((tab) => (
                  <button
                    key={tab.id || 'all'}
                    type="button"
                    onClick={() => setFilterStatus(tab.id)}
                    className={`rounded-lg px-3 py-1.5 text-sm font-medium transition ${
                      filterStatus === tab.id
                        ? 'bg-primary-light text-primary'
                        : 'text-muted hover:bg-slate-50 hover:text-text'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {activeResource === 'boardings' ? (
            <BoardingsByRide
              rides={records}
              loading={loading}
              loadingMore={loadingMore}
              hasMore={Boolean(pagination.hasNext)}
              lookups={lookups}
              onAddBoarding={boardingPermission.canCreate ? openCreateFormForRide : undefined}
              onEditBoarding={boardingPermission.canEdit ? openEditForm : undefined}
              onEditRide={ridePermission.canEdit ? openEditRideForm : undefined}
              onCompleteRide={ridePermission.canEdit ? requestCompleteRide : undefined}
              completingRideId={completingRideId}
              onPrintRoteiro={handlePrintRoteiro}
              printingRideId={printingRideId}
              onDeleteBoarding={boardingPermission.canDelete ? handleDelete : undefined}
              onLoadMore={() => loadRecords((pagination.page || 1) + 1, { append: true })}
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="sticky top-0 z-10 bg-slate-50/95 backdrop-blur">
                  <tr className="border-b border-border text-left text-xs font-semibold uppercase tracking-wide text-muted">
                    {columns.map((column) => (
                      <th key={column} className="px-5 py-3">{COLUMN_LABELS[column] || column}</th>
                    ))}
                    {(resourcePermission.canEdit || resourcePermission.canDelete) && (
                      <th className="px-5 py-3 text-right">Ações</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={columns.length + ((resourcePermission.canEdit || resourcePermission.canDelete) ? 1 : 0)} className="px-5 py-10 text-center text-muted">
                        Carregando registros...
                      </td>
                    </tr>
                  ) : records.length === 0 ? (
                    <tr>
                      <td colSpan={columns.length + ((resourcePermission.canEdit || resourcePermission.canDelete) ? 1 : 0)} className="px-5 py-10 text-center text-muted">
                        Nenhum registro encontrado.
                      </td>
                    </tr>
                  ) : (
                    records.map((record, index) => (
                      <tr
                        key={record.id}
                        className={`border-b border-border transition hover:bg-primary-light/40 ${
                          index % 2 === 0 ? 'bg-white' : 'bg-slate-50/60'
                        }`}
                      >
                        {columns.map((column) => (
                          <td key={column} className="px-5 py-4 align-middle text-text">
                            {displayCell(record, column)}
                          </td>
                        ))}
                        {(resourcePermission.canEdit || resourcePermission.canDelete) && (
                          <td className="px-5 py-4 text-right">
                            <ActionMenu
                              onEdit={resourcePermission.canEdit ? () => openEditForm(record) : undefined}
                              onDelete={resourcePermission.canDelete ? () => handleDelete(record) : undefined}
                            />
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {activeResource !== 'boardings' && (
          <div className="flex flex-col gap-3 border-t border-border px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-sm text-muted">
              Mostrando {pageStart} a {pageEnd} de {pagination.total || 0} registros
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={!pagination.hasPrev}
                onClick={() => loadRecords((pagination.page || 1) - 1)}
                className="inline-flex items-center gap-1 rounded-lg border border-border bg-white px-3 py-2 text-sm text-text disabled:cursor-not-allowed disabled:opacity-50"
              >
                <ChevronLeft size={16} /> Anterior
              </button>
              <span className="rounded-lg bg-slate-100 px-3 py-2 text-sm font-medium text-text">
                {pagination.page || 1}
              </span>
              <button
                type="button"
                disabled={!pagination.hasNext}
                onClick={() => loadRecords((pagination.page || 1) + 1)}
                className="inline-flex items-center gap-1 rounded-lg border border-border bg-white px-3 py-2 text-sm text-text disabled:cursor-not-allowed disabled:opacity-50"
              >
                Próxima <ChevronRight size={16} />
              </button>
            </div>
          </div>
          )}
        </section>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-sm">
          <div className="max-h-[90vh] w-full max-w-4xl overflow-hidden rounded-2xl border border-border bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-border px-6 py-4">
              <div>
                <h2 className="text-lg font-semibold text-text">
                  {editingRecord ? 'Editar' : 'Novo'} {RESOURCE_LABELS[formResource || activeResource]}
                </h2>
                <p className="text-sm text-muted">
                  {formResource === 'rides' && activeResource === 'boardings'
                    ? 'Altere os dados da corrida sem sair dos embarques.'
                    : lockedRideId
                      ? 'Este embarque será vinculado à corrida selecionada.'
                      : 'Preencha os campos abaixo para salvar o registro.'}
                </p>
              </div>
              <button
                type="button"
                onClick={closeForm}
                className="rounded-lg border border-border px-3 py-1.5 text-sm text-muted hover:bg-slate-50"
              >
                Fechar
              </button>
            </div>

            <form onSubmit={handleSubmit} className="max-h-[calc(90vh-88px)] overflow-y-auto px-6 py-5">
              {formError && (
                <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-danger">
                  {formError}
                </div>
              )}
              {showBirthLookup && (
                <div className="mb-5 rounded-2xl border border-border bg-slate-50 p-4">
                  <div className="text-sm font-semibold text-text">Buscar no cadastro do município</div>
                  <p className="mt-1 text-xs text-muted">
                    Informe a data de nascimento para listar os cidadãos e preencher os campos.
                  </p>
                  <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-end">
                    <div className="sm:w-56">
                      <label className="mb-1.5 block text-sm font-medium text-text" htmlFor="birth-search-date">
                        Data de nascimento
                      </label>
                      <input
                        id="birth-search-date"
                        type="date"
                        value={birthSearchDate}
                        onChange={(event) => setBirthSearchDate(event.target.value)}
                        className={inputClassName}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={searchPatientsByBirthDate}
                      disabled={birthSearchLoading}
                      className="inline-flex items-center justify-center gap-2 rounded-xl border border-border bg-white px-4 py-2.5 text-sm font-medium text-text transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      <Search size={16} />
                      {birthSearchLoading ? 'Buscando...' : 'Buscar'}
                    </button>
                  </div>
                  {birthCandidates.length > 0 && (
                    <BirthCandidatePicker
                      candidates={birthCandidates}
                      selectedKey={selectedBirthKey}
                      onSelect={handleSelectBirthCandidate}
                      getLabel={birthCandidateLabel}
                      inputClassName={inputClassName}
                    />
                  )}
                </div>
              )}
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {formFields.map((field) => {
                  if (field.type === 'hidden') return null;
                  return (
                  <React.Fragment key={field.name}>
                    {field.section && (
                      <div className="mt-2 md:col-span-2">
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-semibold uppercase tracking-wide text-primary">
                            {field.section}
                          </span>
                          <div className="h-px flex-1 bg-border" />
                        </div>
                      </div>
                    )}
                    <div className={field.type === 'textarea' || field.full || field.name === 'patient_id' || field.type === 'companion-lookup' ? 'md:col-span-2' : ''}>
                      {field.type !== 'checkbox' && (
                        <label className="mb-1.5 block text-sm font-medium text-text">
                          {field.label}
                          {field.required && <span className="text-danger"> *</span>}
                        </label>
                      )}
                      {field.name === 'ride_id' && lockedRideId && !editingRecord ? (
                        <div className="rounded-xl border border-border bg-slate-50 px-3 py-2.5 text-sm text-text">
                          <div className="font-medium">Corrida já selecionada</div>
                          <div className="text-xs text-muted">
                            {lockedRideOrigin && lockedRideDestination
                              ? `${lockedRideOrigin} → ${lockedRideDestination}`
                              : `#${lockedRideId}`}
                          </div>
                          <input type="hidden" name="ride_id" value={lockedRideId} />
                        </div>
                      ) : (
                        renderField(field)
                      )}
                      {field.hint && (
                        <p className="mt-1 text-xs text-muted">{field.hint}</p>
                      )}
                    </div>
                  </React.Fragment>
                  );
                })}
              </div>

              <div className="mt-6 flex justify-end gap-3 border-t border-border pt-4">
                <button
                  type="button"
                  onClick={closeForm}
                  className="rounded-xl border border-border bg-white px-4 py-2.5 text-sm font-medium text-text hover:bg-slate-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="rounded-xl bg-primary px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
                >
                  {saving ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {rideToComplete && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-sm"
          onClick={closeCompleteRideModal}
        >
          <div
            className="w-full max-w-md overflow-hidden rounded-2xl border border-border bg-white shadow-2xl"
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="complete-ride-title"
          >
            <div className="px-6 pt-6 text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-600">
                <Check size={22} />
              </div>
              <h2 id="complete-ride-title" className="mt-4 text-lg font-semibold text-text">
                Concluir corrida
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-muted">
                Confirme se esta viagem já foi realizada. Os embarques ainda agendados também serão marcados como concluídos.
              </p>
            </div>

            <div className="px-6 py-4">
              <div className="rounded-2xl border border-border bg-slate-50 px-4 py-3 text-left">
                <div className="text-sm font-semibold text-text">
                  {(lookups.cities || []).find((city) => Number(city.id) === Number(rideToComplete.origin_city_id))?.name || 'Origem'}
                  {' → '}
                  {(lookups.cities || []).find((city) => Number(city.id) === Number(rideToComplete.destination_city_id))?.name || 'Destino'}
                </div>
                <div className="mt-1 text-xs text-muted">{displayDate(rideToComplete.starts_at)}</div>
                <div className="mt-2 text-xs text-muted">
                  {(rideToComplete.boardings || []).length} embarque(s)
                  {(rideToComplete.boardings || []).filter((item) => item.status === 'scheduled' || item.status === 'active').length > 0
                    ? ` · ${(rideToComplete.boardings || []).filter((item) => item.status === 'scheduled' || item.status === 'active').length} serão concluídos`
                    : ''}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 border-t border-border px-6 py-4">
              <button
                type="button"
                disabled={Boolean(completingRideId)}
                onClick={closeCompleteRideModal}
                className="rounded-xl border border-border bg-white px-4 py-2.5 text-sm font-medium text-text hover:bg-slate-50 disabled:opacity-60"
              >
                Voltar
              </button>
              <button
                type="button"
                disabled={Boolean(completingRideId)}
                onClick={handleCompleteRide}
                className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-60"
              >
                <Check size={16} />
                {completingRideId ? 'Concluindo...' : 'Concluir corrida'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Transport;
